import React, { useState } from 'react';
import IASideBar from './IASideBar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HomeIcon from '@mui/icons-material/Home';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AnalyticsIcon from '@mui/icons-material/Analytics';

const ProcessNarrativeLibrary = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);
  const handleSubmit = () => {
    console.log('Request submitted:', { name, email });
    setSubmitted(true);
    setTimeout(() => {
      setOpenDialog(false);
      setSubmitted(false);
    }, 2000);
  };

  // Create a theme consistent with your application
  const theme = createTheme({
    palette: {
      primary: {
        main: '#0288d1',
      },
      secondary: {
        main: '#4caf50',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
        },
      },
    },
  });

  // Main content of the page
  const customMainContent = (
    <div>
      <ResponsiveContainer>
        <mui.Box sx={{ padding: '24px' }}>
          {/* Breadcrumbs */}
          <mui.Breadcrumbs 
                        aria-label="breadcrumb"
                        sx={{
                            '& .MuiLink-root': {
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.secondary',
                                fontSize: '0.875rem',
                                transition: 'color 0.2s ease',
                                '&:hover': {
                                    color: 'primary.main'
                                }
                            }
                        }}
                    >
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Typography color="text.primary">Process Narrative</mui.Typography>
                    </mui.Breadcrumbs>

          {/* Header */}
          <mui.Box sx={{ 
            mt: 3, 
            mb: 4, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AssignmentIcon 
                sx={{ 
                  fontSize: 32, 
                  color: 'primary.main',
                  mr: 1,
                }} 
              />
              <mui.Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                Process Narrative
              </mui.Typography>
            </mui.Box>
          </mui.Box>

          {/* Coming Soon Box */}
          <mui.Paper 
            elevation={0}
            sx={{ 
              p: 4, 
              mb: 4, 
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.08)',
              backgroundColor: '#f8fafc',
            }}
          >
            <mui.Typography 
              variant="h5" 
              sx={{ 
                mb: 2, 
                color: 'primary.main',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <DescriptionIcon sx={{ mr: 1 }} />
              Coming Soon
            </mui.Typography>
            
            <mui.Typography 
              variant="body1" 
              sx={{ 
                mb: 3, 
                color: '#505050',
                lineHeight: 1.7,
                fontSize: '1rem',
              }}
            >
              The Process Narrative module will allow you to document, visualize, and analyze your organization's 
              key business processes. This feature is currently under development and will provide tools for creating 
              detailed process descriptions, flowcharts, and control mappings, helping you to streamline operations, 
              identify control points, and ensure regulatory compliance.
            </mui.Typography>
            
            <mui.Button
              variant="contained"
              size="large"
              disableElevation
              onClick={handleOpenDialog}
              sx={{
                px: 4,
                py: 1.2,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 500,
                mt: 2,
              }}
            >
              Request Early Access
            </mui.Button>
          </mui.Paper>

          {/* Features Cards */}
          <mui.Typography 
            variant="h5" 
            sx={{ 
              mb: 3, 
              fontWeight: 500,
              color: 'text.primary',
            }}
          >
            Key Features
          </mui.Typography>

          <mui.Grid container spacing={3} sx={{ mb: 4 }}>
            <mui.Grid item xs={12} md={4}>
              <mui.Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <mui.Avatar
                    sx={{
                      bgcolor: 'rgba(2, 136, 209, 0.1)',
                      color: 'primary.main',
                      width: 48,
                      height: 48,
                      mr: 2,
                    }}
                  >
                    <AssignmentIcon />
                  </mui.Avatar>
                  <mui.Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Structured Documentation
                  </mui.Typography>
                </mui.Box>
                <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Create standardized, detailed narratives of business processes with templates and collaboration tools.
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
            
            <mui.Grid item xs={12} md={4}>
              <mui.Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <mui.Avatar
                    sx={{
                      bgcolor: 'rgba(76, 175, 80, 0.1)',
                      color: 'secondary.main',
                      width: 48,
                      height: 48,
                      mr: 2,
                    }}
                  >
                    <TimelineIcon />
                  </mui.Avatar>
                  <mui.Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Visual Process Mapping
                  </mui.Typography>
                </mui.Box>
                <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Design interactive flowcharts and process diagrams to visualize workflows and identify improvement opportunities.
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
            
            <mui.Grid item xs={12} md={4}>
              <mui.Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <mui.Avatar
                    sx={{
                      bgcolor: 'rgba(2, 136, 209, 0.1)',
                      color: 'primary.main',
                      width: 48,
                      height: 48,
                      mr: 2,
                    }}
                  >
                    <AccountTreeIcon />
                  </mui.Avatar>
                  <mui.Typography variant="h6" sx={{ fontWeight: 500 }}>
                    Control Integration
                  </mui.Typography>
                </mui.Box>
                <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Map controls to specific process steps, link to relevant policies, and track compliance requirements within each process.
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
          </mui.Grid>

          {/* Status message */}
          <mui.Alert 
            severity="info"
            icon={<AnalyticsIcon />}
            sx={{ 
              mb: 4,
              borderRadius: 2,
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <mui.Typography variant="body1" color="primary.dark">
              Our team is working diligently to deliver this feature. We're excited to bring you powerful process documentation tools to enhance your governance and control framework.
            </mui.Typography>
          </mui.Alert>
        </mui.Box>
      </ResponsiveContainer>
      
      {/* Dialog for early access request */}
      <mui.Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: '500px',
            width: '100%',
          }
        }}
      >
        {submitted ? (
          <mui.Box sx={{ p: 4, textAlign: 'center' }}>
            <mui.Avatar
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                color: 'secondary.main',
                width: 80,
                height: 80,
                margin: '0 auto 24px auto',
              }}
            >
              <AssignmentIcon sx={{ fontSize: 40 }} />
            </mui.Avatar>
            <mui.Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Thank You!
            </mui.Typography>
            <mui.Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your early access request has been received. We'll notify you as soon as the Process Narrative module becomes available.
            </mui.Typography>
            <mui.Button
              variant="contained"
              color="primary"
              onClick={handleCloseDialog}
              disableElevation
              sx={{
                px: 4,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
              }}
            >
              Close
            </mui.Button>
          </mui.Box>
        ) : (
          <>
            <mui.DialogTitle
              sx={{
                py: 3,
                px: 3,
                fontWeight: 600,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              }}
            >
              Request Early Access to Process Narrative
            </mui.DialogTitle>
            <mui.DialogContent sx={{ p: 3, pt: 3 }}>
              <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Be among the first to experience our Process Narrative module. Fill in your details below, and we'll notify you when it's ready.
              </mui.Typography>
              <mui.TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                margin="normal"
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <mui.TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                margin="normal"
                variant="outlined"
              />
            </mui.DialogContent>
            <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <mui.Button
                onClick={handleCloseDialog}
                sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                }}
              >
                Cancel
              </mui.Button>
              <mui.Button
                onClick={handleSubmit}
                variant="contained"
                disableElevation
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                }}
              >
                Submit Request
              </mui.Button>
            </mui.DialogActions>
          </>
        )}
      </mui.Dialog>
    </div>
  );

  return (
    <ThemeProvider theme={theme}>
      <IASideBar mainContent={customMainContent} />
    </ThemeProvider>
  );
};

export default ProcessNarrativeLibrary;
